import { useState, useMemo } from "react"
import { v4 as uuid } from "uuid"
import { cloneDeep } from "lodash"
import { useShallow } from "zustand/react/shallow"

// components
import PrePeriod from "./subcomponents/PrePeriod/PrePeriod"
import { PrimeButton } from "components-design-system"
import ResultsTableCreateModal from "../../components/ResultsTableCreateModal/ResultsTableCreateModal"
import GlobalArms from "./subcomponents/GlobalArms/GlobalArms"
import Period from "./subcomponents/Period/Period"

// context
import useAuthoringViewStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useAuthoringViewStore"
import useResultsTableViewStore from "../../../hooks/stores/useResultsTableViewStore"

const RJNewParticipantFlow = ({ schema, participantData, setParticipantData }) => {
  const { readOnly, formView, customTableWidthClassName } = useAuthoringViewStore(
    useShallow((state) => ({
      readOnly: state.readOnly,
      formView: state.formView,
      customTableWidthClassName: state.customTableWidthClassName,
    }))
  )

  const { tableView, participantView, participantRegistries } = useResultsTableViewStore()

  const [showAddPeriodModal, setShowAddPeriodModal] = useState(false)

  const handleAddPeriod = (applyAll) => {
    const id = `period-${uuid()}`
    const startedMilestoneID = `milestone-${uuid()}`
    const completedMilestoneID = `milestone-${uuid()}`

    const template = cloneDeep(schema.properties.templates.period[tableView])
    template.milestone_list[0].id = startedMilestoneID
    template.milestone_list[1].id = completedMilestoneID

    let tempData = { ...participantData }

    for (const registry of participantRegistries) {
      if (applyAll || registry === participantView) {
        const newPeriod = formatNewPeriod({ id, ...template }, registry)
        tempData[registry] = [...participantData[registry], newPeriod]
      } else {
        tempData[registry] = participantData[registry]
      }
    }

    setParticipantData(tempData)
  }

  const formatNewPeriod = (period, type) => {
    if (!participantData?.global_arms?.length) {
      return period
    }

    const armParticipants = participantData.global_arms.map((arm) => ({
      arm_id: arm.id,
      value: "",
      comment: "",
      registry_arm_id: formatArmIds(period.id, arm.id),
    }))

    const newMilestones = []
    for (const milestone of period.milestone_list) {
      let newParticipants = cloneDeep(armParticipants)
      const participants = milestone.participants.concat(newParticipants)
      newMilestones.push({ ...milestone, participants })
    }

    const newDropWithdrawnReasons = []
    for (const reason of period.drop_withdrawn_reason_list) {
      let newParticipants = cloneDeep(armParticipants)
      const participants = reason.participants.concat(newParticipants)
      newDropWithdrawnReasons.push({ ...reason, participants })
    }

    const newPeriod = {
      ...period,
      milestone_list: newMilestones,
      drop_withdrawn_reason_list: newDropWithdrawnReasons,
    }

    if (type === "eudract_periods") {
      newPeriod.arms = participantData.global_arms.map((arm) => ({
        ...arm,
        registry_arm_id: formatArmIds(period.id, arm.id),
      }))
    }

    return newPeriod
  }

  const formatArmIds = (periodId, armId) => {
    let pIds = periodId.split(".")
    let pId = pIds[pIds.length - 1]

    let aIds = armId.split(".")
    let aId = aIds[aIds.length - 1]

    return `${pId}:${"ParticipantFlow-ParticipantFlowGroup." + aId}`
  }

  /**
   * Filters objects from arr2 whose ids are not present in arr1.
   * @param {Array<Object>} arr1 - The first array of objects.
   * @param {Array<Object>} arr2 - The second array of objects.
   * @returns {Array<Object>} - Array of objects from arr2 with unique ids not in arr1.
   */
  const getUniqueObjects = (arr1 = [], arr2 = []) => {
    // Create a Set of ids from arr1 for fast lookup
    const idsInArr1 = new Set(arr1.map((obj) => obj.id))

    // Filter objects in arr2 whose id is not in idsInArr1
    return arr2.filter((obj) => !idsInArr1.has(obj.id))
  }

  const displayNonDuplicateCTGPeriods = () => {
    let uniqCTGPeriods = getUniqueObjects(participantData?.["ctg_periods"], participantData?.["eudract_periods"])

    return uniqCTGPeriods?.map((period, periodIndex) => {
      return (
        <Period
          key={`uniq-period-${periodIndex}`}
          periodIndex={periodIndex}
          participantData={participantData}
          setParticipantData={setParticipantData}
          schema={schema}
          period={period}
          tableView={"eudract"}
          participantView={"eudract_periods"}
        />
      )
    })
  }

  const renderPeriods = () => {
    return (
      <div
        className="flex flex-col"
        style={{ gap: 8 }}
      >
        {participantData?.[participantView]?.map((period, periodIndex) => {
          return (
            <Period
              key={`period-${period.id}-${periodIndex}-${tableView}`}
              periodIndex={periodIndex}
              participantData={participantData}
              setParticipantData={setParticipantData}
              schema={schema}
              period={period}
              tableView={tableView}
              participantView={participantView}
            />
          )
        })}
        {formView.id === "global" && displayNonDuplicateCTGPeriods()}
      </div>
    )
  }

  const classNames = ["results-table", customTableWidthClassName]

  return (
    <div className={classNames.join(" ")}>
      {formView.id !== "ctg" && (
        <PrePeriod
          schema={schema}
          participantData={participantData}
          setParticipantData={setParticipantData}
        />
      )}
      {(participantView === "ctg_periods" || formView.id === "global") && (
        <GlobalArms
          schema={schema}
          participantData={participantData}
          setParticipantData={setParticipantData}
        />
      )}
      {renderPeriods()}
      {readOnly !== true && (
        <PrimeButton
          size="small"
          type="add"
          onClick={() => setShowAddPeriodModal(true)}
          style={{ marginTop: 20 }}
        >
          Add Period
        </PrimeButton>
      )}
      <ResultsTableCreateModal
        header="Add Period"
        onSubmit={handleAddPeriod}
        showModal={showAddPeriodModal}
        setShowModal={() => setShowAddPeriodModal(false)}
      />
    </div>
  )
}

export default RJNewParticipantFlow
